import { Button, Col, Form, message, Row, Space, Spin, Table } from "antd";
import Card from "../components/Card";
import React, { RefObject } from "react";
import { RuleType } from "rc-field-form/lib/interface";
import BaseForm, { FieldFactory } from "../components/Form/BaseForm";
import {
  FormFieldConfig,
  Report,
  SelectOptionsInterface,
  ReportData,
  InputTypes,
  CustomReport,
  User,
} from "../helpers/shared-interfaces";
import { FormInstance } from "antd/lib/form";
import { AxiosResponse } from "axios";
import * as Sentry from "@sentry/browser";
import {
  capitalizeFirstLetter,
  formatSnakeCase,
  request,
  snakeToCamel,
} from "../helpers/utilities";
import { RouteComponentProps } from "react-router-dom";
import moment from "moment";
import ModalCreator from "../components/ModalCreator";
import { PageToolbar } from "../components/PageToolbar";
import ExportData from "../components/ExportData";
import { FileTextOutlined } from "@ant-design/icons";
import { generateDisplayErrorMessage } from "../helpers/utilities";


type ReportVariableType = {
  arguments: string[];
  userId: string;
  name: string[];
  reportId: string;
};
interface Option {
  name: InputTypes;
  rule: RuleType;
  options: string[];
}
const FieldToInputMapping: Record<string, Option> = {
  integer: {
    name: "number",
    rule: "number",
    options: [],
  },
  string: {
    name: "string",
    rule: "string",
    options: [],
  },
  boolean: {
    name: "select",
    rule: "string",
    options: ["true", "false"],
  },
  date: {
    name: "defaultDate",
    rule: "object",
    options: [],
  },
};

export const GET_REPORTS = `
query {
    reports {
      id
      name
      parameters
      query
    }
}
`;

export const GENERATE_REPORTS = `
mutation GenerateReport($arguments: [String!], $id: ID!) {
    generateReport(arguments: $arguments, id: $id) {
        columns
        data
        errors {
          description
          property
        }
    }
  }
`;

export const CREATE_CUSTOM_REPORTS = `
mutation CreateCustomReport($arguments: [String!]!, $reportId: ID!, $name: String!, $userId: String!) {
  createCustomReport(arguments: $arguments, reportId: $reportId, name: $name, userId: $userId) {
    customReport {
      id
      name
      parameterValues
      report {
        id
        name
        parameters
        query
      }
    }
    errors {
      description
      property
    }
  }
}
`;

export const GENERATE_REPORT_CSV = `
  mutation GenerateReportCSV($id: ID!, $arguments: [String!]!) {
    generateReportCsv(id: $id, arguments: $arguments) {
      url
      errors {
        description
        property
      }
    }
  }
`;

export const GET_USER = `
  query {
    currentUser {
      id
      name
      customReports {
        id
        name
        parameterValues
        report {
          id
          name
          parameters
          query
        }
      }
    }
  }
`;

export const CustomReportUI = (props: {
  reference: RefObject<FormInstance>;
}): JSX.Element => {
  const formField: FormFieldConfig = {
    name: "name",
    label: "Custom Report Name",
    inputType: "string",
    placeholder: "Custom Report Name",
    rule: "string",
    required: true,
    colConfig: {
      span: 20,
    },
    formItemStyle: {
      textTransform: "capitalize",
    },
  };

  return (
    <>
      <Spin spinning={false}>
        <Row>
          <Col
            span={24}
            style={{
              marginBottom: "20px",
            }}
          >
            <div
              style={{
                marginTop: "10px",
              }}
            >
              <BaseForm
                layout="vertical"
                reference={props.reference}
                fields={[formField]}
                fieldsToHide={[]}
              ></BaseForm>
            </div>
          </Col>
        </Row>
      </Spin>
    </>
  );
};

export const generateColumn = (column: unknown) => {
  return {
    title: capitalizeFirstLetter(formatSnakeCase(column as string)),
    dataIndex: column,
    key: column,
    align: "center" as const,
    render(text: string): JSX.Element {
      return <span>{text}</span>;
    },
  };
};

interface State {
  isLoading: boolean;
  showSaveReportModal: boolean;
  reports: Report[];
  selectedReport?: Report;
  currentUser?: User;
  selectedCustomReport?: CustomReport;
  reportFields: FormFieldConfig[];
  customReportFields: FormFieldConfig[];
  reportData?: ReportData;
  searchValue: string;
}

export class ViewReport extends React.Component<RouteComponentProps, State> {
  reportReference: RefObject<FormInstance>;
  customReportReference: RefObject<FormInstance>;
  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      isLoading: true,
      showSaveReportModal: false,
      reports: [],
      currentUser: undefined,
      customReportFields: [],
      selectedCustomReport: undefined,
      reportFields: [],
      reportData: undefined,
      selectedReport: undefined,
      searchValue: "",
    };
    this.reportReference = React.createRef<FormInstance>();
    this.customReportReference = React.createRef<FormInstance>();
  }

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reportReference = ref;
  };

  setCustomRef = (ref: React.RefObject<FormInstance>): void => {
    this.customReportReference = ref;
  };

  getReports = async () => {
    interface Response {
      data: {
        reports: Report[];
      };
    }
    try {
      const { data }: AxiosResponse<Response> = await request({
        query: GET_REPORTS,
      });

      this.setState({
        reports: data.data.reports,
      });
    } catch (error) {
      message.error(`Failed to fetch reports - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  };

  getUser = async () => {
    interface Response {
      data: {
        currentUser: User;
      };
    }
    try {
      const { data }: AxiosResponse<Response> = await request({
        query: GET_USER,
      });

      this.setState({
        currentUser: data.data.currentUser,
      });
    } catch (error) {
      message.error(`Failed to fetch user reports - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  };

  async componentDidMount() {
    this.setState({ isLoading: true });
    try {
      await Promise.all([this.getReports(), this.getUser()]);
    } finally {
      this.setState({ isLoading: false });
    }
  }

  toggleSaveReportModal = () => {
    this.setState((oldState) => ({
      showSaveReportModal: !oldState.showSaveReportModal,
    }));
  };

  getFormValues = (): string[] => {
    if (!this.reportReference.current) return [];
    //pick all the form values except the reportId and reportButton
    const fieldsValueObj: Record<string, unknown> =
      this.reportReference.current.getFieldsValue();
    const { selectedReport, selectedCustomReport } = this.state;
    const report = selectedReport || selectedCustomReport?.report;
    const parameters = report?.parameters || {};
    const args: string[] = [];

    Object.keys(parameters).forEach((key) => {
      const fieldName = snakeToCamel(key);
      let value = fieldsValueObj[fieldName];

      if (fieldName.toLowerCase().includes("date") && value) {
        value = moment(value as any).format();
      }

      if (value !== undefined && value !== null) {
        args.push(String(value));
      } else {
        args.push(""); // Push empty string for optional but missing values
      }
    });

    return args;
  };

  createCustomReport = async () => {
    const fieldsToBeValidated = ["reportId"];
    if (!this.reportReference.current) return;

    let customReportData;

    const { currentUser } = this.state;

    try {
      await this.reportReference.current.validateFields(
        fieldsToBeValidated,
      );
      customReportData =
        await this.customReportReference.current?.validateFields(["name"]);
    } catch (error) {
      message.error(`You need to select a report - ${generateDisplayErrorMessage(error)}`);
      return;
    }

    const { selectedReport, selectedCustomReport } = this.state;
    const reportId = selectedCustomReport
      ? selectedCustomReport.report.id
      : selectedReport?.id;

    if (!reportId) {
      message.error("You need to select a report");
      return;
    }

    const args = this.getFormValues();

    interface Response {
      data: {
        createCustomReport: {
          customReport: CustomReport;
        };
      };
    }

    this.setState({ isLoading: true });

    const variables: ReportVariableType = {
      arguments: args,
      userId: currentUser?.id as string,
      name: customReportData?.name,
      reportId: reportId as string,
    };

    try {
      const response: AxiosResponse<Response> = await request({
        query: CREATE_CUSTOM_REPORTS,
        variables,
      });

      if (response.data.data.createCustomReport.customReport) {
        message.success("Custom report saved successfully ");
        this.setState({ showSaveReportModal: false });
        this.getUser();
      } else {
        message.error("Failed to save custom report ");
      }
    } catch (error) {
      message.error(`An error occurred, please try again later - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }

    this.setState({ isLoading: false });
  };

  handleShowSaveCustomReportField = () => {
    const { selectedCustomReport, reportFields } = this.state;
    const saveCustomReportField: FormFieldConfig = {
      id: "saveReportButton",
      name: "saveReportButton",
      inputType: "button",
      value: "Save As Custom Report",
      required: false,
      className: "btn-secondary",
      handleClick: this.toggleSaveReportModal,
      colConfig: {
        offset: 1,
        span: 3,
      },
    };
    const hasCustomReportField = reportFields.some(
      (report) => report.id === saveCustomReportField.id,
    );
    if (!selectedCustomReport && !hasCustomReportField) {
      this.setState((oldState) => ({
        reportFields: [...oldState.reportFields, saveCustomReportField],
      }));
    }
  };

  generateReport = async () => {
    if (!this.reportReference.current) return;

    try {
      await this.reportReference.current.validateFields();
    } catch (error) {
      return;
    }

    const args = this.getFormValues();

    interface Response {
      data: {
        generateReport: ReportData;
      };
    }

    const { selectedReport, selectedCustomReport } = this.state;

    if (!selectedReport && !selectedCustomReport) {
      message.error("You need to select a report type");
      return;
    }

    const reportId = selectedCustomReport
      ? selectedCustomReport.report.id
      : selectedReport?.id;
    if (!reportId) {
      message.error("Could not determine report ID. Please re-select the report type.");
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse<Response> = await request({
        query: GENERATE_REPORTS,
        variables: {
          id: reportId,
          arguments: args,
        },
      });
      this.setState({ reportData: response.data.data.generateReport });
      this.handleShowSaveCustomReportField();
    } catch (error) {
      message.error(`An error occurred, please try again later - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }

    this.setState({ isLoading: false });
  };

  handleCustomReportSelect = (id: string) => {
    const customReport = this.state.currentUser?.customReports?.find(
      (customReport) => customReport.id === id,
    ) as CustomReport;
    this.setState({ selectedCustomReport: customReport });
    this.generateFields(customReport.report, customReport.parameterValues);
  };

  handleReportSelect = (id: string): void => {
    if (!id) {
      this.setState({
        selectedReport: undefined,
        reportFields: [],
        customReportFields: [],
        selectedCustomReport: undefined,
        reportData: undefined,
      });
      return;
    }

    if (id === "customReport") {
      const options = this.state.currentUser?.customReports?.map((option) => {
        return { id: option.id, name: capitalizeFirstLetter(option.name) };
      });
      const formField: FormFieldConfig = {
        id: "userReport",
        name: "userReport",
        inputType: "select",
        rule: "string",
        required: false,
        placeholder: "Select User Report",
        changeHandler: this.handleCustomReportSelect,
        selectOptions: options,
        allowClear: true,
        className: "pt-filter-select",
        colConfig: {
          offset: 1,
          span: 3,
        },
      };
      this.setState({
        customReportFields: [formField],
        reportFields: [],
      });
      return;
    }
    const report = this.state.reports.find((report) => report.id === id);
    this.setState({
      selectedReport: report,
      reportFields: [],
      customReportFields: [],
      selectedCustomReport: undefined,
    });
    //clear previous fields
    if (report?.parameters) {
      const fields = Object.keys(
        report?.parameters as Record<string, unknown>,
      ).map(snakeToCamel);
      this.reportReference.current?.resetFields([...fields, "userReport"]);
    }
    if (report) {
      this.generateFields(report as Report);
    }
  };

  downloadReportCsv = async (): Promise<void> => {
    interface Response {
      data: {
        generateReportCsv: {
          url: string;
        };
      };
    }

    const report = this.state.selectedReport;

    if (!report) {
      message.error("Please select a valid report before downloading.");
      this.setState({ isLoading: false });
      return;
    }

    this.setState({ isLoading: true });
    try {
      const args = this.getFormValues();
      const response: AxiosResponse<Response> = await request({
        query: GENERATE_REPORT_CSV,
        variables: { id: report.id, arguments: args },
      });
      window.open(response.data.data.generateReportCsv.url);
    } catch (error) {
      message.error(`An error occurred, please try again later - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }

    this.setState({ isLoading: false });
  };

  handleCustomExport = (selectedFields: Array<any>): void => {
    this.downloadReportCsv();
  };

  generateFields = (
    report: Report,
    defaultValues?: Record<string, unknown>,
  ): void => {
    if (report.parameters) {
      const fields: FormFieldConfig[] = Object.keys(report.parameters).map(
        (key) => {
          const value = (report.parameters as Record<string, string>)[key];
          const inputType = FieldToInputMapping[value as string];
          const formField: FormFieldConfig = {
            id: snakeToCamel(key),
            name: snakeToCamel(key),
            inputType: inputType.name,
            rule: inputType.rule,
            required: false,
            placeholder: capitalizeFirstLetter(formatSnakeCase(key)),
            allowClear: true,
            className: "pt-filter-select",
          };

          //set default value based on type
          const defaultValue = defaultValues ? defaultValues[key] : "";
          if (key.toLowerCase().includes("date") && defaultValue) {
            formField["defaultPickerValue"] = moment(
              defaultValue as moment.MomentInput,
            );
          } else {
            formField["initialValue"] =
              defaultValue as typeof formField.initialValue;
          }

          //set select options if options is present
          if (inputType.options.length > 0) {
            formField["selectOptions"] = inputType.options.map((option) => {
              return { id: option, name: capitalizeFirstLetter(option) };
            });
          }
          return formField;
        },
      );

      fields.push({
        id: "reportButton",
        name: "reportButton",
        inputType: "button",
        value: "Generate Report",
        required: false,
        className: "btn-secondary",
        handleClick: this.generateReport,
      });

      this.setState({
        reportFields: fields,
      }, () => {
        const values: Record<string, any> = {};
        fields.forEach(field => {
          if (field.initialValue !== undefined) values[field.name] = field.initialValue;
          if (field.defaultPickerValue !== undefined) values[field.name] = field.defaultPickerValue;
        });
        this.reportReference.current?.setFieldsValue(values);
      });
    } else {
      this.setState({
        reportFields: [
          {
            id: "reportButton",
            name: "reportButton",
            inputType: "button",
            value: "Generate Report",
            required: false,
            className: "btn-secondary",
            handleClick: this.generateReport,
          },
        ],
      });
    }
  };

  generateReportTable = (): JSX.Element => {
    const { reportData, searchValue } = this.state;
    const fieldColumns: any[] = [];
    const dataSource: any[] = [];

    reportData?.columns.forEach((column, i) => {
      if (i < 5) {
        fieldColumns.push(generateColumn(column));
      }
    });

    let dataRows = reportData?.data || [];
    if (searchValue) {
      dataRows = dataRows.filter((row) =>
        Object.values(row).some((val) =>
          String(val).toLowerCase().includes(searchValue.toLowerCase()),
        ),
      );
    }

    dataRows.forEach((row) => {
      const columns = reportData?.columns || [];
      let source: Record<string, unknown> = {};
      for (const key in columns) {
        const column = columns[key];
        const value = row[key];
        source[column] = value;
      }
      dataSource.push(source);
      source = {};
    });

    return (
      <Table
        loading={this.state.isLoading}
        bordered={true}
        columns={fieldColumns}
        dataSource={dataSource}
      />
    );
  };

  getFilteredDataCount = (): number => {
    const { reportData, searchValue } = this.state;
    if (!reportData || !searchValue) return reportData?.data.length || 0;

    return reportData.data.filter((row) =>
      Object.values(row).some((val) =>
        String(val).toLowerCase().includes(searchValue.toLowerCase()),
      ),
    ).length;
  };

  render() {
    const { reports, reportFields, customReportFields } = this.state;
    let reportOptions: SelectOptionsInterface[] = reports.map((report) => {
      return { id: report.id, name: report.name };
    });
    reportOptions = [
      ...reportOptions,
      { id: "customReport", name: "Custom Report" },
    ];

    const options: FormFieldConfig[] = [...customReportFields, ...reportFields];

    return (
      <>
        {this.state.showSaveReportModal &&
          ModalCreator({
            title: "Custom Report",
            okType: "primary",
            okText: "Done",
            isLoading: this.state.isLoading,
            onOkay: this.createCustomReport,
            showModal: this.state.showSaveReportModal,
            toggleModal: this.toggleSaveReportModal,
            showFooter: true,
            inscription: CustomReportUI({
              reference: this.customReportReference,
            }),
          })}

        <Card>
          <div className="auditLogs-table-container">
            <PageToolbar modelName="records"
              searchValue={this.state.searchValue}
              onSearchChange={(e) => this.setState({ searchValue: e.target.value })}
              searchPlaceholder="Search result..."
              totalCount={this.state.reportData?.data.length || 0}
              currentDataCount={
                this.state.searchValue
                  ? this.getFilteredDataCount()
                  : this.state.reportData?.data.length || 0
              }
              page={1}
              pageSize={this.state.reportData?.data.length || 25}
              primaryAction={
                <ExportData
                  model="report"
                  disabled={!this.state.reportData}
                  buttonText="Export Report"
                  handleExport={this.handleCustomExport}
                  customQueryableFields={
                    this.state.reportData?.columns
                      ? [{ "Report Columns": this.state.reportData.columns }]
                      : undefined
                  }
                />
              }
              filters={
                <Form ref={this.reportReference} component={false}>
                  <PageToolbar.Filters
                    configs={[
                      {
                        label: "Report Type",
                        options: reportOptions.map(opt => ({ value: opt.id, label: opt.name })),
                        selectedValues: [this.state.selectedReport?.id || (this.state.selectedCustomReport ? "customReport" : "")].filter(Boolean),
                        onChange: (values) => this.handleReportSelect(values[0]),
                        width: 250,
                        mode: "single",
                        icon: <FileTextOutlined />,
                      },
                      ...options.filter(opt => opt.name === "userReport").map((opt: FormFieldConfig) => ({
                        label: opt.placeholder || opt.name,
                        options: opt.selectOptions?.map(so => ({ value: String(so.value || so.id), label: so.name })) || [],
                        selectedValues: [opt.value].filter(Boolean),
                        onChange: (values: string[]) => opt.changeHandler && opt.changeHandler(values[0]),
                        onSearch: opt.changeHandler,
                        width: 200,
                        mode: "single",
                      })) as any
                    ]}
                  >
                    <Space wrap>
                      {options.filter(opt => opt.inputType !== "button" && opt.name !== "userReport").map((opt: FormFieldConfig) => (
                        <Form.Item
                          name={opt.name}
                          key={opt.id}
                          noStyle
                          initialValue={opt.initialValue || opt.defaultPickerValue}
                        >
                          {FieldFactory(opt)}
                        </Form.Item>
                      ))}
                      {options.filter(opt => opt.inputType === "button").map(opt => (
                        <Button
                          key={opt.id}
                          type="primary"
                          size="large"
                          className="base-button"
                          onClick={opt.handleClick}
                        >
                          {opt.value}
                        </Button>
                      ))}
                    </Space>
                  </PageToolbar.Filters>
                </Form>
              }
            />
            {this.generateReportTable()}
          </div>
        </Card>
      </>
    );
  }
}
